import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  type ReceivedBidV2Dto,
  type SourcingEventV2Dto,
  type ViewDocumentAsOwnerDto,
  useGetOrganizationQuery,
  useListBidV2SForEventQuery,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { DocumentsAsOwnerTable } from "../../../common/documents/DocumentsAsOwnerTable";
import { useDisplayDate } from "../../../common/formatting/useDisplayDate";
import { urls } from "../../../urls";
import { isEventAwarded } from "./bidOutcome";
import { OrgInviteStatusTag } from "./OrgInviteStatusTag";
import { SupplierStatusTag } from "./SupplierStatusTag";
import { isBidContentUnsealed } from "./bidAccess";
import { buildSupplierRows, isBidDeadlinePassed, type SupplierRow } from "./supplierRows";
import { competitionStage } from "./qualification/stage";
import { RoundPriceDevelopmentSection } from "./negotiation/RoundPriceDevelopmentSection";
import { useRoundSelection } from "./rounds/RoundSelectionContext";

interface Props {
  event: SourcingEventV2Dto;
}

export const BidsTab = ({ event }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const displayDate = useDisplayDate();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { rounds, selectedRound } = useRoundSelection();
  const eventId = event.id;
  const { data, isLoading, isError } = useListBidV2SForEventQuery({ eventId });
  const isPrivate = event.eventContext.type === "Private";
  // The BFF already withholds public bids until the submission deadline; we gate again on the
  // client so a server regression could never make who-submitted observable while bidding is
  // open. Private events mirror V1: who delivered is always visible to the buyer, while the bid
  // content (documents, detail page) opens early only for organizations allowed to see delivered
  // bids before the deadline — the BFF redacts the content accordingly.
  const deadlinePassed = isBidDeadlinePassed(event);
  const revealBidContent = isBidContentUnsealed(event, selectedOrg);
  // Two-stage: before the invitation there is no tender phase at all, so the supplier table
  // (driven by interest registrations) would only mislead. Show where the competition stands.
  const stage = competitionStage(event);
  const beforeTenderPhase = stage === "REQUESTS_OPEN" || stage === "QUALIFICATION";
  const allBids = useMemo(
    () => ((deadlinePassed || isPrivate) && data ? data.bids : []),
    [deadlinePassed, isPrivate, data],
  );
  // ADR-033 D2: the tab shows the selected competition stage — a round's bids, or the
  // original competition's when no round is selected.
  const bids = useMemo(
    () =>
      allBids.filter((bid) =>
        selectedRound ? bid.forNegotiationRound === selectedRound.id : bid.forNegotiationRound == null,
      ),
    [allBids, selectedRound],
  );
  const rows = useMemo(() => buildSupplierRows(event, bids), [event, bids]);
  const awarded = isEventAwarded(event.status);

  const documentsModal = useDisclosure();
  const [selectedBidId, setSelectedBidId] = useState<string | null>(null);
  const selectedBid = bids.find((bid) => bid.id === selectedBidId);
  const selectedDocuments = useMemo(
    () => (selectedBid ? getSubmittedBidDocuments(selectedBid) : []),
    [selectedBid],
  );
  const openDocuments = (bid: ReceivedBidV2Dto) => {
    setSelectedBidId(bid.id);
    documentsModal.onOpen();
  };
  const closeDocuments = () => {
    documentsModal.onClose();
    setSelectedBidId(null);
  };

  if (beforeTenderPhase) {
    return (
      <Flex justify="center" py="16">
        <Text color="smMuted">
          {t("The tender phase starts when the invitation to tender has been sent.")}
        </Text>
      </Flex>
    );
  }

  return (
    <Stack spacing="5">
      {isLoading ? (
        <Flex justify="center" py="16">
          <Spinner color="smPrimary" />
        </Flex>
      ) : isError ? (
        <Flex justify="center" py="16">
          <Text color="smError">{t("Could not load suppliers.")}</Text>
        </Flex>
      ) : rows.length === 0 ? (
        <Flex justify="center" py="16">
          <Text color="smMuted">
            {stage === "TENDERING"
              ? t("{{count}} invited suppliers — bids are revealed when the deadline passes.", {
                  count: (event.eventContext.type === "Public" ? event.eventContext : null)?.qualificationRound?.invitation?.invitedOrganizationIds.length ?? 0,
                })
              : isPrivate
                ? t("No invites have been added")
                : t("No suppliers have registered interest yet.")}
          </Text>
        </Flex>
      ) : (
        <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Supplier")}</Th>
                  <Th>{t("Status")}</Th>
                  {revealBidContent && <Th>{t("Submitted")}</Th>}
                  {revealBidContent && <Th>{t("Documents")}</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row) => {
                  const bid = row.bid;
                  const documents = bid ? getSubmittedBidDocuments(bid) : [];
                  const detailUrl =
                    bid && revealBidContent ? urls.v2.sourcingEvents.bidDetail.go(eventId, bid.id) : null;
                  return (
                    <Tr
                      key={row.organizationId}
                      _hover={{ bg: "smBackgroundSecondary" }}
                      cursor={detailUrl ? "pointer" : undefined}
                      onClick={detailUrl ? () => navigate(detailUrl) : undefined}
                    >
                      <Td fontWeight="medium" color="smText">
                        <SupplierName row={row} />
                      </Td>
                      <Td>
                        {!bid && row.inviteStatus ? (
                          <OrgInviteStatusTag status={row.inviteStatus} />
                        ) : (
                          <SupplierStatusTag bidStatus={bid?.status} awarded={awarded} size="sm" />
                        )}
                      </Td>
                      {revealBidContent && (
                        <Td color="smMuted">{bid?.submittedAt ? displayDate(bid.submittedAt) : "–"}</Td>
                      )}
                      {revealBidContent && (
                        <Td>
                          {documents.length > 0 ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (bid) openDocuments(bid);
                              }}
                            >
                              {t("View documents")} ({documents.length})
                            </Button>
                          ) : (
                            <Text color="smMuted" fontSize="sm">
                              {t("No documents")}
                            </Text>
                          )}
                        </Td>
                      )}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {rounds.length > 0 && (
        <RoundPriceDevelopmentSection eventId={eventId} rounds={rounds} bids={allBids} />
      )}
      <Modal isOpen={documentsModal.isOpen} onClose={closeDocuments} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t("Submitted bid documents")}
            {selectedBid?.ownerName ? `: ${selectedBid.ownerName}` : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6">
            <DocumentsAsOwnerTable documents={selectedDocuments} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

const SupplierName = ({ row }: { row: SupplierRow }) => {
  const { t } = useTranslation();
  const { data: org, isLoading } = useGetOrganizationQuery(
    { orgId: row.organizationId },
    { skip: !!row.organizationName },
  );
  if (!row.organizationName && isLoading) return <Spinner size="xs" color="smPrimary" />;
  return <>{row.organizationName || org?.name || t("Unknown")}</>;
};

const getSubmittedBidDocuments = (bid: ReceivedBidV2Dto): ViewDocumentAsOwnerDto[] =>
  [
    ...(bid.documents ?? []),
    ...((bid.qualificationResponses ?? []).flatMap((response) => response.evidenceDocuments ?? [])),
  ].filter((document, index, documents) => documents.findIndex((d) => d.id === document.id) === index);
